import AddIcon from "@mui/icons-material/Add";
import { Box, Skeleton, styled } from "@mui/material";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ComponentErrorBoundary } from "core/components/ComponentErrorBoundary";
import { CustomLink } from "core/components/CustomLink";
import { ErrorComponent } from "core/components/ErrorComponent";
import type { GridAreaContainerProps } from "core/components/GridAreaContainer";
import { GridAreaContainer } from "core/components/GridAreaContainer";
import type { TFunction } from "core/entities";
import { useBrand } from "core/hooks/useBrand";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useLocale } from "core/hooks/useLocale";
import { useRegion } from "core/hooks/useRegion";
import { endOfDay, startOfDay } from "date-fns";
import type { EventSummaryDto } from "features/NewsEvents/resources/NewsEventsDefs";
import type { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { BigEventWidgetTile } from "./BigEventWidgetTile";
import { EventsWidgetTile } from "./EventsWidgetTile";

const CONTAINER_NAME = "event-widget-paper";
const GRID_AREA = "events";

const DynamicStack = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(4),
  flexDirection: "column",
  "& > *:first-child": {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [theme.containerQueries(CONTAINER_NAME).up(375)]: {
    flexDirection: "row",
    "& > *:first-child": {
      borderBottom: "none",
    },
  },
}));

function Wrapper({
  children,
  t,
  containerProps,
}: PropsWithChildren<{
  t: TFunction;
  containerProps?: Omit<
    GridAreaContainerProps,
    "title" | "children" | "gridArea"
  >;
}>) {
  return (
    <ComponentErrorBoundary fallBackBoxProps={{ sx: { gridArea: GRID_AREA } }}>
      <GridAreaContainer
        {...containerProps}
        gridArea={GRID_AREA}
        title={t("gridAreaContainerTitle")}
        stackProps={{
          className: "event-widget",
          ...containerProps?.stackProps,
        }}
        BottomRightSlot={
          <Button
            href="/events"
            LinkComponent={CustomLink}
            startIcon={<AddIcon />}
          >
            {t("seeAllEvents")}
          </Button>
        }
      >
        {children}
      </GridAreaContainer>
    </ComponentErrorBoundary>
  );
}

export function EventsWidgetV2({
  hideIfEmpty = false,
  containerProps,
}: {
  hideIfEmpty?: boolean;
  containerProps?: Omit<
    GridAreaContainerProps,
    "title" | "children" | "gridArea"
  >;
}) {
  const { t } = useTranslation("NewsEvents", {
    keyPrefix: "Next:NewsEvents:EventsWidget",
  });
  const locale = useLocale();
  const { brandCode } = useBrand();
  const region = useRegion();
  const { data, isError, isLoading } = useCentrixApi({
    path: "/api/news-events/articles-v2/Events/public",
    parameters: {
      query: {
        Brands: [brandCode],
        Regions: region ? [region] : [],
        Locale: locale,
        Sorting: "startDate asc",
        AvailableFromMax: endOfDay(new Date()).toUTCString(),
        AvailableToMin: startOfDay(new Date()).toUTCString(),
        EndDateMin: startOfDay(new Date()).toUTCString(),
        MaxResultCount: 4,
      },
    },
  });

  if (isError) {
    if (hideIfEmpty) {
      return null;
    }
    return (
      <Wrapper t={t} containerProps={containerProps}>
        <Paper
          sx={{
            p: 3,
            height: "100%",
          }}
        >
          <ErrorComponent />
        </Paper>
      </Wrapper>
    );
  }

  if (isLoading || !data || !Array.isArray(data.items)) {
    if (hideIfEmpty) {
      return null;
    }
    return (
      <Wrapper t={t} containerProps={containerProps}>
        <Skeleton variant="rounded" height="100%" />
      </Wrapper>
    );
  }
  if (hideIfEmpty && data.items.length === 0) {
    return null;
  }

  return (
    <Wrapper t={t} containerProps={containerProps}>
      <EventWidgetDisplay items={data.items} />
    </Wrapper>
  );
}

export function EventWidgetDisplay({ items }: { items: EventSummaryDto[] }) {
  const { t } = useTranslation("NewsEvents", {
    keyPrefix: "Next:NewsEvents:EventsWidget",
  });

  if (items.length === 0) {
    return (
      <Paper
        sx={{
          p: 3,
          height: "100%",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          {t("noEvents")}
        </Typography>
      </Paper>
    );
  }
  const [firstEvent, ...otherEvents] = items;
  return (
    <Paper
      sx={{
        p: 3,
        height: "100%",
        containerType: "inline-size",
        containerName: CONTAINER_NAME,
      }}
    >
      <DynamicStack>
        {firstEvent && (
          <BigEventWidgetTile
            id={firstEvent.id!}
            startDate={firstEvent.startDate}
            endDate={firstEvent.endDate}
            title={firstEvent.title ?? ""}
            address={firstEvent.address}
            url={firstEvent.url}
          />
        )}
        {items.length > 1 && (
          <Stack
            sx={{
              flex: 1,
              "& > *:not(:last-of-type)": {
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                paddingBottom: 2,
              },
            }}
            direction="column"
            gap={2}
          >
            {otherEvents.map((event) => (
              <EventsWidgetTile
                key={event.id}
                id={event.id!}
                startDate={event.startDate}
                title={event.title ?? ""}
                address={event.address}
              />
            ))}
          </Stack>
        )}
      </DynamicStack>
    </Paper>
  );
}
